// Maps Panda chat interactions to their analytics event name + properties.
// Platform-agnostic — each platform's own analytics service does the dispatch.

import type { Message } from '@ag-ui/client';
import { EVENTS } from '../../constants/analytics';
import { AG_UI_ROLE } from '../agent/ag-ui.constants';
import type { HubSource } from '../agent/ag-ui.constants';
import type { FeedbackValue } from '../panda.constants';
import type { PANDA_SEARCH_RESULT_TYPE } from '../panda.constants';

export type MessageActionEventProperties = {
  /** Null until the first send creates the thread. */
  conversationId: string | null;
  messageId: string;
};

// Declared as type aliases, not interfaces: only an alias gets the implicit
// index signature that satisfies the tracker's `Record<string, unknown>`.

/** Identifies one document answer card within an assistant message. */
export type DocumentEventProperties = {
  conversationId: string | null;
  messageId: string;
  documentId: string;
  /** 1-based position of the document answer within the message. */
  position: number;
};

export type CitationEventProperties = {
  conversationId: string | null;
  messageId: string;
  documentId: string;
  /** 1-based position of the citation within the document answer's citations. */
  position: number;
  /** Misspelled to match the property v1 has been reporting since launch. */
  expended: boolean;
};

export type MessageSentEventProperties = {
  conversationId: string;
  messageId: string;
  /** User messages in the conversation, counting the one just sent. */
  depth: number;
};

export type ClearChatEventProperties = {
  conversationId: string | null;
  /** User messages in the conversation when it was cleared. */
  depth: number;
};

/** How a hub source is named in analytics (`type`), per PLT-4720. */
const HUB_ANALYTICS_TYPE: Record<HubSource, string> = { asa: 'ASA' };

export function hubAnalyticsType(source: HubSource): string {
  return HUB_ANALYTICS_TYPE[source];
}

/** The knowledge-hub (ASA) "fallback" offer: the assistant message carrying it. */
export type FallbackEventProperties = {
  conversationId: string | null;
  messageId: string;
  /** Join key to the offer's other events; the offer id itself. */
  impression_id: string;
  type: string;
};

/** The run's decision on whether an offer was made, on the run's last assistant message. */
export type FallbackResponseEventProperties = {
  conversationId: string | null;
  messageId: string;
  type: string;
  response: 'shown' | 'hidden';
};

/** The run's decision on whether the search page shows an answer card. */
export type SearchAnswerResponseEventProperties = {
  /** The search context id current when the run decided (it rotates on filter changes). */
  contextId?: string;
  response: 'shown' | 'hidden';
};

/** The Panda card reported through the search page's standard clicked event. */
export type SearchAnswerClickedEventProperties = {
  term: string;
  searchSessionId?: string;
  contextId?: string;
  /** The card's own impression — the join key back to its RESULTS_IMPRESSION. Undefined only without a search context, when the rows' clicks omit it too. */
  impression_id: string | undefined;
  /** The search run's id — no conversation exists yet at this point. */
  id: string;
  /** The chat conversation the click opens — joins this row to the AI_CHAT events that follow. */
  conversationId: string;
  resultType: typeof PANDA_SEARCH_RESULT_TYPE;
};

/**
 * `depth` counts user turns only — the agent's message list also holds
 * assistant/tool/activity entries, so its raw length over-reports.
 */
export function conversationDepth(messages: Message[]): number {
  return messages.filter(({ role }) => role === AG_UI_ROLE.USER).length;
}

// Feedback `type` when the thumb is cleared (deselected) rather than positive/negative.
const FEEDBACK_CANCELED = 'canceled';
// Feedback and copy act on the whole message; `entity` stays in the payload for existing dashboards.
const MESSAGE_ENTITY = 'message';

// Loose bag by necessity: payload shapes vary per event and the platform
// trackers forward them opaquely.
type TrackEventFn = (eventName: string, properties: Record<string, unknown>) => void;

/** Binds each Panda event name to the payload shape it must be reported with. */
export function createPandaAnalytics(trackEvent: TrackEventFn) {
  return {
    trackFeedback: (value: FeedbackValue, properties: MessageActionEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.FEEDBACK_V2, {
        ...properties,
        entity: MESSAGE_ENTITY,
        type: value ?? FEEDBACK_CANCELED,
      }),

    trackCopy: (properties: MessageActionEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.COPY_TO_CLIPBOARD, {
        ...properties,
        entity: MESSAGE_ENTITY,
      }),

    trackMessageSent: (properties: MessageSentEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.MESSAGE, properties),

    trackClearChat: (properties: ClearChatEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.CLEAR_CHAT, properties),

    /** The source document behind an answer was opened. */
    trackResourceOpen: (properties: DocumentEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.RESOURCE_OPEN, properties),

    /** An answer's citations section was expanded. Collapsing is not reported. */
    trackOpenReferences: (properties: DocumentEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.OPEN_REFERENCES, properties),

    /** A single citation quote was expanded or collapsed ("see more" / "see less"). */
    trackToggleReference: (properties: CitationEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.TOGGLE_REFERENCE, properties),

    /** The run decided whether to offer a hub (ASA) answer. Once per run that ran the lane. */
    trackFallbackResponse: (properties: FallbackResponseEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.FALLBACK_RESPONSE, properties),

    /** The offer button was clicked ("Show ASA guidance"). */
    trackFallbackClick: (properties: FallbackEventProperties) =>
      trackEvent(EVENTS.AI_CHAT.FALLBACK_CLICK, properties),

    /** The run decided whether the search page shows an answer. Once per run. */
    trackSearchAnswerResponse: (properties: SearchAnswerResponseEventProperties) =>
      trackEvent(EVENTS.SEARCH.PANDA_RESPONSE, properties),

    /** The card's "show more" as the search page's standard clicked event. */
    trackAnswerClicked: (properties: SearchAnswerClickedEventProperties) =>
      trackEvent(EVENTS.SEARCH.RESULT_CLICKED, properties),
  };
}

export type PandaAnalytics = ReturnType<typeof createPandaAnalytics>;
